<script>
    import {
        getContext,
        onMount,
        onDestroy,
        createEventDispatcher,
        tick
    } from "svelte";
    import { to } from "@API";
    import { Policy } from "@Store";

    const dispatch = createEventDispatcher();

    let { program } = getContext("PROGRAM");
    let { policyID } = getContext("CONTEXT");

    export let billingData;

    $: programData = JSON.parse(program);

    let stripe;
    let elements;
    let card;
    let cardElement;
    let cardValid = false;
    let cardError = '';
    let loading = false;
    let isSetup = false;

    onMount(() => {
        if (billingData && !isSetup) {
            setupForm();
        }
    });

    onDestroy(() => {
        if (card) {
            card.destroy();
            card = null;
            isSetup = false;
        }
    });

    $: if (billingData && !isSetup && cardElement) {
        setupForm();
    }

    function setupForm() {
        if (isSetup) return;
        isSetup = true;

        // Initialize Stripe
        stripe = Stripe(programData.payment_gateway.publishable_key);
        elements = stripe.elements();

        // Style for the card element
        const style = {
            base: {
                color: '#32325d',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': {
                    color: '#aab7c4'
                }
            },
            invalid: {
                color: '#fa755a',
                iconColor: '#fa755a'
            }
        };

        // Create card element
        card = elements.create('card', { style: style, hidePostalCode: true });

        // Mount to DOM
        if (cardElement) {
            card.mount(cardElement);

            // Listen for card changes
            card.on('change', (event) => {
                if (event.error) {
                    cardError = event.error.message;
                    cardValid = false;
                } else {
                    cardError = '';
                }

                if (event.complete) {
                    cardValid = true;
                    dispatch('ready');
                }
            });
        }
    }

    export async function processPayment() {
        if (!cardValid) {
            cardError = 'Please enter valid card details';
            return;
        }

        loading = true;
        cardError = '';

        // Find country and region names
        const country = programData.countries.find(c => c.id == billingData.country);
        const region = programData.regions.find(r => r.id == billingData.region);

        // Create token with billing details
        const { token, error } = await stripe.createToken(card, {
            name: billingData.name,
            address_line1: billingData.address,
            address_city: billingData.city,
            address_state: region?.name || '',
            address_zip: billingData.postal_code,
            address_country: country?.name || ''
        });

        if (error) {
            cardError = error.message;
            dispatch('error', error);
            loading = false;
            return;
        }

        // Send token to backend
        let [payError, response] = await to(
            Insurecert.API.policy($policyID).pay({
                token: token.id,
                billing_info: billingData
            })
        );

        if (response && response.status === 'success') {
            // Refetch the policy to get updated data (policy_number, status, etc.)
            let [, updatedPolicy] = await to(Insurecert.API.policy($policyID).get());
            if (updatedPolicy) {
                $Policy = updatedPolicy;
            }
            dispatch('complete', response);
        } else {
            cardError = payError?.data?.message || 'Payment failed';
            dispatch('error', payError);
        }

        loading = false;
    }

</script>

<div class="stripe-payment">
    <div id="card-element" bind:this={cardElement}>
        <!-- Stripe card element will be inserted here -->
    </div>

    {#if cardError}
        <div id="card-errors" class="error">{cardError}</div>
    {/if}
</div>

<style>
    .stripe-payment {
        margin: 20px;
    }

    h3 {
        margin-bottom: 15px;
    }

    #card-element {
        border: 1px solid #ccc;
        border-radius: 5px;
        padding: 15px;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    }

    .error {
        color: var(--red);
        margin-top: 10px;
        padding: 10px;
        background: #fef0f0;
        border-radius: 5px;
    }
</style>